/**
=========================================================
* Elite 247 Dashboard - Recent Activity Component
=========================================================
*/

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import TimelineItem from "examples/Timeline/TimelineItem";

// API
import { adminApi } from "services/api";
import { useAuth } from "context/AuthContext";
import { loadRecentActivities, viewAllPath } from "./recentActivities";

function RecentActivity() {
  const { hasPermission } = useAuth();
  const viewAll = viewAllPath(hasPermission);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentActivities();
  }, []);

  const fetchRecentActivities = async () => {
    try {
      setActivities(await loadRecentActivities(adminApi, hasPermission));
    } catch (error) {
      console.error("Failed to fetch recent activities:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const getActivityLink = (activity) => {
    switch (activity.type) {
      case "contact":
        return "/contact-submissions";
      case "application":
        return "/bootcamp-applications";
      case "consultation":
        return "/consultations";
      default:
        return "#";
    }
  };

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox pt={3} px={{ xs: 2, sm: 3 }}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MDTypography variant="h6" fontWeight="medium">
            Recent Activity
          </MDTypography>
          <Icon sx={{ color: ({ palette: { info } }) => info.main }}>notifications_active</Icon>
        </MDBox>
        {activities.length > 0 && (
          <MDBox mt={0} mb={2}>
            <MDTypography variant="button" color="text" fontWeight="regular">
              <MDTypography display="inline" variant="body2" verticalAlign="middle">
                <Icon sx={{ color: ({ palette: { success } }) => success.main }}>check_circle</Icon>
              </MDTypography>
              &nbsp;
              <MDTypography variant="button" color="text" fontWeight="medium">
                {activities.length}
              </MDTypography>{" "}
              recent activities
            </MDTypography>
          </MDBox>
        )}
      </MDBox>
      <MDBox p={2}>
        {loading ? (
          <MDBox textAlign="center" py={3}>
            <MDTypography variant="body2" color="text">
              Loading activities...
            </MDTypography>
          </MDBox>
        ) : activities.length === 0 ? (
          <MDBox textAlign="center" py={3}>
            <Icon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}>inbox</Icon>
            <MDTypography variant="body2" color="text">
              No recent activities
            </MDTypography>
          </MDBox>
        ) : (
          <>
            {activities.map((activity, index) => (
              <TimelineItem
                key={activity._id || index}
                color={activity.color}
                icon={activity.icon}
                title={activity.title}
                description={activity.description}
                dateTime={formatTime(activity.createdAt)}
                lastItem={index === activities.length - 1}
              />
            ))}
            {viewAll && (
              <MDBox display="flex" justifyContent="center" mt={2}>
                <Tooltip title="View all activities">
                  <MDButton
                    component={Link}
                    to={viewAll}
                    variant="text"
                    color="info"
                    size="small"
                    sx={({ breakpoints }) => ({ [breakpoints.down("md")]: { minHeight: 40 } })}
                  >
                    <Icon sx={{ mr: 1 }}>visibility</Icon>
                    View All
                  </MDButton>
                </Tooltip>
              </MDBox>
            )}
          </>
        )}
      </MDBox>
    </Card>
  );
}

export default RecentActivity;
